/**
 * "Your quick wins" (owner, 2026-10-14: "AI can generate an ordering of content OF OUR APP which FIXES THEIR VIOLATIONS as part of the
 * onboarding experience for QUICK WINS"; "like print the allergen thing if it was an issue"). Per location, from its last inspection's
 * cited items (public inspection data: no personal health information), an ORDERED list of the app's own content that fixes them:
 *   plan    the item's "Let's fix that" plan (citedplan.ts): done when its "Fix it today" part is done.
 *   print   a printout tied to the item by a source in the repo (PRINT_TIES below): "Print it", then "Posted" counts it done.
 *   check   the item's daily check, when it isn't on yet (lastinspection.ts Coverage "add"): "Add it".
 * Each win has a catalog id ("plan:4.3.59", "print:sign:handwashing-sign", "check:22"); nothing here is written by a model.
 *
 * The order:
 *   baseline (always; used when the AI is off, fails, is over its cap, or for the demo and test accounts): not done first, then
 *            critical items, then wins a person can finish in under 5 minutes, then repeats, then the rest; ties by fewest taps,
 *            then by id (deterministic).
 *   AI       optional (services/api/src/official/quickwins.ts, QUICK_WINS_AI=on): the model sees only the ids, short titles, minutes and
 *            the kind of place, and returns an order with a one-line "why first". applyAiOrder keeps only known ids, once each, and
 *            appends any it left out in baseline order.
 * Minutes and taps are ASSUMED estimates (about 2 minutes a fix-today step, 2 for printing and posting, 1 for adding a check), used
 * only to order, never shown as a promise.
 *
 * Printouts (PRINT_TIES): read from the Signs and forms data (signs.ts signById + appliesHere) and the Paper logs data (templates.ts),
 * tied to an FDA item only by a source: a sign by the Food Code section Guide 3-B lists under that item and the sign's own citation
 * names (the same ties as feat/search-printouts' printouts.ts SIGN_ITEMS: when that lands, read its catalog instead), a paper log by
 * the inspection form's records column (inspectionform.ts FORM_ITEMS tags "tpl:<id>"), the date label by its "food:label" tag. The
 * health check-in and return-to-work logs are private and never a win.
 */
import { anchorOf, fixedLine, planFixed, type CitedPlanView } from "./citedplan.ts";
import { FORM_ITEMS } from "./inspectionform.ts";
import type { Coverage, LastInspectionView } from "./lastinspection.ts";
import type { PermitType } from "./model.ts";
import { appliesHere, signById } from "./signs.ts";
import type { UsState } from "./states/types.ts";
import { templatesFor } from "./templates.ts";

export type WinKind = "plan" | "print" | "check";
export interface QuickWin {
  /** The catalog id: "plan:<code>", "print:<printout id>", "check:<item>". */
  id: string;
  kind: WinKind;
  /** The cited code it serves (a print win's "Posted" tap is kept on this code's plan). */
  code: string;
  item: number | null;
  title: string;
  /** One line: the first fix not done yet, or where the printout goes. */
  line: string;
  minutes: number;
  taps: number;
  critical: boolean;
  repeat: boolean;
  done: boolean;
  /** The one action: "Let's fix that" (the plan, at step 1), "Print it" (the printout), "Add it" (the check). */
  action: { label: string; to: string };
  /** Print wins: the printout's id, for the "Posted" tap. */
  print?: string;
  /** The reward line once done. */
  reward: string;
  /** The model's one line on why it comes first (AI order only). */
  why?: string;
  /** A plan win whose plan is the generic one (its title is the department's own words, never sent to a model). */
  generic?: true;
}

export const LETS_FIX = "Let's fix that";
export const PRINT_IT = "Print it";
export const POSTED = "Posted";
export const ADD_IT = "Add it";
export const QUICK_WINS_TITLE = "Your quick wins";
export const QUICK_WINS_MAX = 3;
/** Under this many minutes a win counts as quick (owner: "steps a person can finish in under 5 minutes"). */
export const QUICK_MINUTES = 5;

/** The place a printout must apply to (signs.ts appliesHere). */
export interface WinPlace { state?: UsState; county?: string; permitType?: PermitType }

/** Sign → FDA item, each by the Food Code section Guide 3-B lists under the item and the sign's own citation names (test-checked). */
export const PRINT_SIGN_TIES: readonly { sign: string; item: number; cite: string }[] = [
  { sign: "handwashing-sign", item: 10, cite: "6-301.14" },
  { sign: "consumer-advisory", item: 25, cite: "3-603.11" },
  { sign: "allergen-notice", item: 37, cite: "3-602.12" },
  { sign: "clean-tableware-notice", item: 47, cite: "3-304.16" },
  { sign: "employee-health-agreement", item: 3, cite: "2-103.11" },
  { sign: "employee-health-agreement", item: 4, cite: "2-201.11" },
];
/** Never a win: private health logs (appsearch.ts T4). */
export const PRINT_PRIVATE = new Set(["health_checkin", "ready_to_work"]);

export interface PrintTie { id: string; title: string; where: string; to: string; items: number[]; tie: string }

/** The printouts tied to these items at this place (signs first, then the date label, then paper logs). */
export function printTiesFor(items: readonly number[], p: WinPlace): PrintTie[] {
  const want = new Set(items);
  if (!want.size) return [];
  const out: PrintTie[] = [];
  const signs = new Map<string, number[]>();
  for (const t of PRINT_SIGN_TIES) if (want.has(t.item)) signs.set(t.sign, [...(signs.get(t.sign) ?? []), t.item]);
  for (const [id, its] of signs) {
    const d = signById(id, p.state);
    if (!d || (p.permitType && !appliesHere(d, { county: p.county, permitType: p.permitType }, p.state))) continue;
    out.push({ id: `sign:${id}`, title: d.title.en, where: d.where, to: `/signs#sign-${id}`, items: its, tie: PRINT_SIGN_TIES.filter((t) => t.sign === id).map((t) => `Food Code ${t.cite}`).join(", ") });
  }
  const labelItems = FORM_ITEMS.filter((f) => f.tags?.includes("food:label") && want.has(f.n)).map((f) => f.n);
  if (labelItems.length) out.push({ id: "label:food", title: "Food date labels", where: "On each container you prep or open", to: "/food/label", items: labelItems, tie: "the form's food labels record" });
  const logs = p.permitType ? templatesFor(p.permitType, p.state) : [];
  for (const t of logs) {
    if (PRINT_PRIVATE.has(t.id) || t.id === "task_done") continue;
    const its = FORM_ITEMS.filter((f) => f.tags?.includes(`tpl:${t.id}`) && want.has(f.n)).map((f) => f.n);
    if (its.length) out.push({ id: `log:${t.id}`, title: `${t.title}: paper sheet`, where: "On the wall or a clipboard", to: `/paper?sheet=${encodeURIComponent(t.id)}#print`, items: its, tie: "the form's records column" });
  }
  return out;
}

const PLAN_STEP_MINUTES = 2, PRINT_MINUTES = 2, CHECK_MINUTES = 1;

/** Every win for this view, unordered. `posted`: the printout ids marked "Posted" here (from the plans' docs). */
export function quickWinCatalog(v: Pick<LastInspectionView, "items" | "repeats">, p: WinPlace = {}): QuickWin[] {
  const repeats = new Set(v.repeats.map((r) => r.code.replace(/\*+\s*$/, "").trim()));
  const out: QuickWin[] = [];
  const seenPrint = new Set<string>();
  const items = [...v.items].sort((a, b) => Number(b.critical) - Number(a.critical));
  for (const i of items) {
    const repeat = repeats.has(i.code.replace(/\*+\s*$/, "").trim());
    const plan: CitedPlanView | null = i.plan;
    const item = plan?.item ?? i.fda?.[0] ?? null;
    if (plan) {
      const left = plan.now.steps.filter((s) => !s.done);
      const fixed = planFixed(plan);
      out.push({
        id: `plan:${i.code}`, kind: "plan", code: i.code, item, title: plan.title, line: left[0]?.say ?? plan.now.reward ?? plan.title,
        minutes: Math.max(1, left.length) * PLAN_STEP_MINUTES, taps: Math.max(1, left.length), critical: i.critical, repeat, done: fixed,
        action: { label: LETS_FIX, to: `/inspection#${anchorOf(plan)}` }, reward: `Fixed today: ${plan.thing === "it" ? i.title : plan.thing}.`,
        ...(plan.kind === "fallback" ? { generic: true as const } : {}),
      });
    }
    // One printout per cited item (its first tie that is not already a win): three paper logs for one item would crowd out the other fixes.
    for (const t of printTiesFor(i.fda ?? [], p).filter((x) => !seenPrint.has(x.id)).slice(0, 1)) {
      seenPrint.add(t.id);
      out.push({
        id: `print:${t.id}`, kind: "print", code: i.code, item: t.items[0] ?? null, title: `Print: ${t.title}`, line: t.where,
        minutes: PRINT_MINUTES, taps: 2, critical: i.critical, repeat, done: !!plan?.posted?.includes(t.id),
        action: { label: PRINT_IT, to: t.to }, print: t.id, reward: `Posted: ${t.title}.`,
      });
    }
    const c: Coverage = i.cover;
    if (c.kind === "add") {
      out.push({
        id: `check:${c.item}`, kind: "check", code: i.code, item: c.item, title: `Add it: ${c.title}`, line: "One tap. It asks every day after.",
        minutes: CHECK_MINUTES, taps: 1, critical: i.critical, repeat, done: false, action: { label: ADD_IT, to: `/inspection#li-item-${i.code}` }, reward: `On your checklist: ${c.title}.`,
      });
    }
  }
  // One win per id (two codes can share an item's check).
  const ids = new Set<string>();
  return out.filter((w) => (ids.has(w.id) ? false : (ids.add(w.id), true)));
}

/** The baseline order (deterministic): not done, critical, under 5 minutes, repeats, then fewest taps, then id. */
export function baselineOrder(wins: readonly QuickWin[]): QuickWin[] {
  const key = (w: QuickWin): (number | string)[] => [w.done ? 1 : 0, w.critical ? 0 : 1, w.minutes < QUICK_MINUTES ? 0 : 1, w.repeat ? 0 : 1, w.taps, w.id];
  return [...wins].sort((a, b) => {
    const ka = key(a), kb = key(b);
    for (let i = 0; i < ka.length; i++) if (ka[i] !== kb[i]) return ka[i]! < kb[i]! ? -1 : 1;
    return 0;
  });
}

export const WHY_MAX = 120;
/**
 * A model's order, cleaned (owner: "the server validates every id against the allowed set (drop unknowns, dedupe, append any missing
 * in baseline order)"). Only not-done wins are reordered; done ones stay after them. A "why" is one plain line: no links, no
 * markup, at most WHY_MAX characters; anything else is dropped. Returns what was dropped, for the log.
 */
export function applyAiOrder(baseline: readonly QuickWin[], raw: unknown): { wins: QuickWin[]; dropped: string[]; used: boolean } {
  const byId = new Map(baseline.map((w) => [w.id, w]));
  const list = raw && typeof raw === "object" && Array.isArray((raw as { order?: unknown }).order) ? (raw as { order: unknown[] }).order : null;
  if (!list) return { wins: [...baseline], dropped: [], used: false };
  const out: QuickWin[] = [], seen = new Set<string>(), dropped: string[] = [];
  for (const x of list) {
    const id = typeof x === "string" ? x : x && typeof x === "object" && typeof (x as { id?: unknown }).id === "string" ? (x as { id: string }).id : null;
    const w = id ? byId.get(id) : undefined;
    if (!id || !w || w.done || seen.has(id)) { dropped.push(String(id ?? "?").slice(0, 60)); continue; }
    seen.add(id);
    const why = x && typeof x === "object" ? cleanWhy((x as { why?: unknown }).why) : null;
    out.push(why ? { ...w, why } : { ...w });
  }
  for (const w of baseline) if (!seen.has(w.id) && !w.done) out.push({ ...w });
  for (const w of baseline) if (w.done) out.push({ ...w });
  return { wins: out, dropped, used: seen.size > 0 };
}
function cleanWhy(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const s = v.replace(/\s+/g, " ").trim();
  if (!s || s.length > WHY_MAX || /https?:|www\.|[<>`*_#[\]]/.test(s)) return null;
  return s;
}

export interface QuickWinsView {
  title: string;
  /** The top wins not done yet (at most QUICK_WINS_MAX). */
  top: QuickWin[];
  /** "1 of 5 fixed" over every win here. */
  line: string;
  done: number;
  total: number;
  /** The reward lines of the wins done here, newest order kept. */
  rewards: string[];
  /** The next prompt: the first win after the top ones, or null. */
  next: string | null;
  /** Which order: the fixed baseline or the model's (cleaned). */
  order: "baseline" | "ai";
  /** Every win, in this order (done ones last): what the API hands a model to reorder. */
  all: QuickWin[];
}

export function quickWinsView(ordered: readonly QuickWin[], order: QuickWinsView["order"] = "baseline", max = QUICK_WINS_MAX): QuickWinsView | null {
  if (!ordered.length) return null;
  const left = ordered.filter((w) => !w.done), done = ordered.filter((w) => w.done);
  const top = left.slice(0, max);
  const after = left[max];
  return {
    title: QUICK_WINS_TITLE, top, line: fixedLine(done.length, ordered.length), done: done.length, total: ordered.length,
    rewards: done.map((w) => w.reward), next: after ? `Next: ${after.title}` : null, order, all: [...left, ...done],
  };
}

// ── The model's input and answer (the API sends this; core owns the words and the schema) ─────────────────────────────

export const QUICK_WINS_SYSTEM = [
  "You order a food business's quick wins after a health inspection. Each win is content that already exists in the app.",
  "Put first what makes food safer soonest with the least effort: critical items, then the fastest, then repeats.",
  "Return every id you are given, once, using only the ids given. For each, one plain line under 120 characters on why it is there.",
  "Never invent steps, rules, numbers, prices or promises. No links, no markup.",
].join(" ");
/** What a model is told for a generic plan's win: never the department's own words (run-17 R17-03). */
export const GENERIC_WIN_TITLE = "Your plan: this item";
/** What the model sees: ids, short titles, minutes, flags and the kind of place. Never a name, an address, a person or a note. */
export function quickWinsPrompt(wins: readonly QuickWin[], kind: string): { kind: string; wins: { id: string; title: string; minutes: number; critical: boolean; repeat: boolean }[] } {
  return { kind: kind.slice(0, 40), wins: wins.filter((w) => !w.done).map((w) => ({ id: w.id, title: w.generic ? GENERIC_WIN_TITLE : w.title.slice(0, 80), minutes: w.minutes, critical: w.critical, repeat: w.repeat })) };
}
export const QUICK_WINS_SCHEMA = {
  type: "object", additionalProperties: false, required: ["order"],
  properties: { order: { type: "array", items: { type: "object", additionalProperties: false, required: ["id", "why"], properties: { id: { type: "string" }, why: { type: "string" } } } } },
} as const;
/** The cache key: the record, the kind of place (the model is told it) and the progress. A changed inspection, a tap or a new check makes a new order. */
export const quickWinsKey = (date: string, wins: readonly QuickWin[], kind = "") => `${date}|${kind.slice(0, 40)}|${wins.map((w) => `${w.id}:${w.done ? 1 : 0}`).sort().join(",")}`;

/** Every fixed string the quick wins write (the copy tests read it). */
export const QUICK_WINS_WORDS: readonly string[] = [LETS_FIX, PRINT_IT, POSTED, ADD_IT, QUICK_WINS_TITLE, "One tap. It asks every day after."];
